import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  FormGroup,
  Label,
  Input,
  Button,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
} from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import SupplierService from "../../../services/OrderingServices/SupplierService";
import PurchaseService from "../../../services/OrderingServices/PurchaseService";
import Select from "react-select";
import PaymentTypeServices from "../../../services/OrderingServices/PaymentTypeService";
import Flatpickr from "react-flatpickr";
import toast from "react-hot-toast";

const validationSchema = Yup.object({
  supplierId: Yup.number().required("Supplier is required").min(1, "Invalid supplier"),
  purchaseIds: Yup.array().of(Yup.number()).nullable(),
  paymentAmount: Yup.number().required("Amount is required").min(0.01, "Amount must be greater than 0"),
  paymentDate: Yup.date().required("Payment date is required"),
  paymentTypeId: Yup.number().required("Payment method is required").min(1, "Invalid payment method"),
  bankName: Yup.string(),
  chequeNo: Yup.string(),
  chequeDate: Yup.date(),
  chequeAmount: Yup.number().min(0, "Cheque amount must be >= 0"),
  transactionReference: Yup.string(),
  remarks: Yup.string(),
});

const SupplierPaymentForm = () => {
  const [supplierData, setSupplierData] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [paymentTypeData, setPaymentTypeData] = useState([]);
  const [supplierBalance, setSupplierBalance] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const breadcrumbItems = [{ title: "Back To List", onClick: () => navigate(-1) }];

  const fetchPaymentType = async () => {
    try {
      const response = await PaymentTypeServices.getList();
      setPaymentTypeData(response);
    } catch (error) { console.log(error); }
  };

  const fetchPurchase = async () => {
    try {
      const response = await PurchaseService.getList();
      setPurchaseData(response?.items || []);
    } catch (error) { console.log(error); }
  };

  const fetchSupplier = async () => {
    try {
      const response = await SupplierService.getList({ pageNumber: 1, perPage: 9999 });
      setSupplierData(response || []);
    } catch (error) { console.log(error); }
  };

  useEffect(() => {
    fetchSupplier();
    fetchPurchase();
    fetchPaymentType();
  }, []);

  useEffect(() => {
    if (location.state?.supplierId && supplierData.length > 0) {
      const selected = supplierData.find(s => s.id === location.state.supplierId);
      if (selected) {
        handleSupplierChange({ value: selected.id, label: selected.name });
      }
    }
  }, [supplierData, location.state]);

  const formik = useFormik({
    initialValues: {
      supplierId: null,
      purchaseIds: [],
      paymentTypeId: null,
      paymentAmount: 0,
      paymentDate: new Date().toISOString().slice(0, 10),
      paymentMethod: "",
      bankName: "",
      chequeNo: "",
      chequeDate: new Date().toISOString().slice(0, 10),
      chequeAmount: 0,
      transactionReference: "",
      remarks: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          supplierId: parseInt(values.supplierId) || 0,
          purchaseIds: values.purchaseIds || [],
          paymentAmount: parseFloat(values.paymentAmount) || 0,
          paymentDate: new Date(values.paymentDate).toISOString(),
          paymentMethod: values.paymentMethod || "", // This is the ID as string
          bankName: values.bankName || "",
          chequeNo: values.chequeNo || "",
          chequeDate: values.chequeDate ? new Date(values.chequeDate).toISOString() : new Date().toISOString(),
          chequeAmount: parseFloat(values.chequeAmount) || 0,
          transactionReference: values.transactionReference || "",
          remarks: values.remarks || "",
        };

        await SupplierService.supplierPayment(payload);
        toast.success("Payment added successfully");
        formik.resetForm();
        navigate("/supplier-payment");
      } catch (error) {
        console.log(error);
        toast.error(error?.response?.data?.message || "Failed to add payment");
      }
    },
  });

  const supplierOptions = supplierData.map((item) => ({ value: item.id, label: item.name }));
  const purchaseOptions = purchaseData.map((item) => ({ 
    value: item.purchaseId || item.id, 
    label: item.referenceNo || item.refrenceNo || item.voucherNo || `Purchase #${item.purchaseId || item.id}` 
  }));
  const paymentTypeOptions = paymentTypeData.map((item) => ({ value: item.id, label: item.name, code:item.code }));

  const handleSupplierChange = async (selected) => {
    const supplierId = selected ? selected.value : null;
    formik.setFieldValue("supplierId", supplierId);
    formik.setFieldValue("purchaseIds", []);
    formik.setFieldValue("paymentAmount", 0);
    
    console.log("🚀 ~ handleSupplierChange ~ supplierId:", supplierId)
    if (supplierId) {
      try {
        const response = await SupplierService.getPendingPurchases(supplierId);
        setPurchaseData(response || []);
        
        // Also fetch total supplier balance
        const balanceResponse = await SupplierService.getSupplierBalance(supplierId);
        setSupplierBalance(balanceResponse?.balance || 0);
      } catch (error) {
        console.error("Error fetching supplier data:", error);
        setPurchaseData([]);
        setSupplierBalance(0);
      }
    } else {
      setPurchaseData([]);
      setSupplierBalance(0);
    }
  };

  const handlePurchaseChange = (selectedOptions) => {
    const ids = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    formik.setFieldValue("purchaseIds", ids);

    // Sum up the total amounts of selected purchases
    const total = ids.reduce((acc, id) => {
      const purchase = purchaseData.find((p) => (p.purchaseId || p.id) === id);
      // Use amountDue (from pending-purchases API), or outstandingBalance, or totalAmount
      const amount = purchase?.amountDue ?? purchase?.outstandingBalance ?? purchase?.totalAmount ?? 0;
      return acc + amount;
    }, 0);
    formik.setFieldValue("paymentAmount", total);
  };

  const handlePaymentTypeChange = (selected) => {
    formik.setFieldValue("paymentTypeId", selected ? selected.value : null);
    formik.setFieldValue("paymentMethod", selected ? selected.value.toString() : ""); 
  };

  const selectedPaymentType = paymentTypeData.find(pt => pt.id === formik.values.paymentTypeId);
  const isCash = selectedPaymentType?.code?.toUpperCase() === "CASH";
  const showBankFields = formik.values.paymentTypeId && !isCash;

  // Calculate total due based on selection or supplier balance
  const totalDueAmount = formik.values.purchaseIds.length > 0
    ? formik.values.purchaseIds.reduce((acc, id) => {
        const purchase = purchaseData.find((p) => (p.purchaseId || p.id) === id);
        const amount = purchase?.amountDue ?? purchase?.outstandingBalance ?? purchase?.totalAmount ?? 0;
        return acc + amount;
      }, 0)
    : (formik.values.supplierId ? supplierBalance : 0);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,.15)" : "none",
      "&:hover": { borderColor: "#86b7fe" },
      fontSize: "0.875rem",
    }),
    option: (base) => ({ ...base, fontSize: "0.875rem" }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  const sectionStyle = {
    background: "#f8f9fc",
    borderRadius: "10px",
    padding: "20px 20px 4px",
    marginBottom: "20px",
    border: "1px solid #eef0f7",
  };

  const sectionHeaderStyle = {
    fontSize: "0.75rem",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    color: "#6c757d",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Supplier Payment"
          pageTitle="Suppliers"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <Row className="g-4 align-items-start">

            {/* ─── LEFT: All form sections ─── */}
            <Col lg={8}>
              <Card className="border-0" style={{ borderRadius: "14px", boxShadow: "0 2px 16px rgba(64,81,137,0.08)" }}>
                <CardBody className="p-4">

                  {/* Card Header */}
                  <div className="d-flex align-items-center gap-3 mb-4 pb-3" style={{ borderBottom: "1.5px solid #eef0f7" }}>
                    {/* <div style={{
                      width: "40px", height: "40px", borderRadius: "10px",
                      background: "linear-gradient(135deg, #405189 0%, #0ab39c 100%)",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <i className="ri-bank-card-line text-white" style={{ fontSize: "18px" }}></i>
                    </div> */}
                    <div>
                      <h5 className="mb-0 fw-bold text-dark">Supplier Payment</h5>
                      <p className="mb-0 text-muted small">Fill in the details below to record a supplier payment</p>
                    </div>
                  </div>
                  {/* Section 1 — Supplier & Purchase */}
                  <div style={sectionStyle}>
                    <div style={sectionHeaderStyle}>
                      <i className="ri-store-2-line" style={{ color: "#405189" }}></i>
                      Supplier Purchase
                    </div>
                    <Row>
                      
                      <Col lg={12}>
                        <FormGroup>
                          <Label className="fw-medium small text-dark">Supplier <span className="text-danger">*</span></Label>
                          <Select
                            options={supplierOptions}
                            value={supplierOptions.find((opt) => opt.value === formik.values.supplierId) || null}
                            onChange={handleSupplierChange}
                            onBlur={formik.handleBlur}
                            placeholder="Select supplier..."
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                          />
                          {formik.touched.supplierId && formik.errors.supplierId && (
                            <div className="text-danger small mt-1">{formik.errors.supplierId}</div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={12}>
                        <FormGroup>
                          <Label className="fw-medium small text-dark">Purchase Reference(s)</Label>
                          <Select
                            isMulti
                            options={purchaseOptions}
                            value={purchaseOptions.filter((opt) => formik.values.purchaseIds.includes(opt.value))}
                            onChange={handlePurchaseChange}
                            onBlur={formik.handleBlur}
                            placeholder="Select purchase(s)..."
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            closeMenuOnSelect={false}
                          />
                          {formik.touched.purchaseIds && formik.errors.purchaseIds && (
                            <div className="text-danger small mt-1">{formik.errors.purchaseIds}</div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Detailed List for Multiple Selections */}
                    {formik.values.purchaseIds.length > 0 && (
                      <div className="mt-2 mb-3 p-3 bg-white border rounded-3 shadow-sm">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-bold small text-muted text-uppercase">Selected Bills Detail</span>
                          <span className="badge bg-soft-info text-info">{formik.values.purchaseIds.length} Selected</span>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: "200px", overflowY: "auto" }}>
                          <Table size="sm" className="align-middle mb-0">
                            <thead className="table-light">
                              <tr style={{ fontSize: "11px" }}>
                                <th>Ref No.</th>
                                <th className="text-end">Due Amount</th>
                              </tr>
                            </thead>
                            <tbody style={{ fontSize: "13px" }}>
                              {formik.values.purchaseIds.map((id) => {
                                const p = purchaseData.find((item) => (item.purchaseId || item.id) === id);
                                return (
                                  <tr key={id}>
                                    <td className="fw-medium text-dark">
                                      {p?.referenceNo || p?.refrenceNo || p?.voucherNo || `ID: ${id}`}
                                    </td>
                                    <td className="text-end fw-bold text-danger">
                                      Rs. {(p?.amountDue ?? p?.outstandingBalance ?? p?.totalAmount ?? 0).toLocaleString()}
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr className="table-light fw-bold">
                                <td>Total Selected</td>
                                <td className="text-end text-primary">Rs. {formik.values.paymentAmount.toLocaleString()}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Section 2 — Payment Details */}
                  <div style={sectionStyle}>
                    <div style={sectionHeaderStyle}>
                      <i className="ri-money-dollar-circle-line" style={{ color: "#0ab39c" }}></i>
                      Payment Details
                    </div>
                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label className="fw-medium small text-dark">Payment Amount <span className="text-danger">*</span></Label>
                          <div style={{ position: "relative" }}>
                            <span style={{
                              position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)",
                              color: "#6c757d", fontSize: "0.875rem", zIndex: 1,
                            }}>Rs.</span>
                            <Input
                              type="number"
                              id="paymentAmount"
                              name="paymentAmount"
                              value={formik.values.paymentAmount}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              style={{ paddingLeft: "36px", borderRadius: "8px", fontSize: "0.875rem" }}
                            />
                          </div>
                          {formik.touched.paymentAmount && formik.errors.paymentAmount && (
                            <div className="text-danger small mt-1">{formik.errors.paymentAmount}</div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label className="fw-medium small text-dark">Payment Date <span className="text-danger">*</span></Label>
                          <Input
                            type="date"
                            id="paymentDate"
                            name="paymentDate"
                            value={formik.values.paymentDate}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            style={{ borderRadius: "8px", fontSize: "0.875rem" }}
                          />
                          {formik.touched.paymentDate && formik.errors.paymentDate && (
                            <div className="text-danger small mt-1">{formik.errors.paymentDate}</div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label className="fw-medium small text-dark">Payment Method <span className="text-danger">*</span></Label>
                          <Select
                            options={paymentTypeOptions}
                            value={paymentTypeOptions.find((opt) => opt.value === formik.values.paymentTypeId) || null}
                            onChange={handlePaymentTypeChange}
                            onBlur={formik.handleBlur}
                            placeholder="Select method..."
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                          />
                          {formik.touched.paymentTypeId && formik.errors.paymentTypeId && (
                            <div className="text-danger small mt-1">{formik.errors.paymentTypeId}</div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  {/* Section 3 — Bank / Cheque Details (conditional) */}
                  {showBankFields && (
                    <div style={{ ...sectionStyle, border: "1px solid #e3e8f5", background: "#f5f7fd" }}>
                      <div style={sectionHeaderStyle}>
                        <i className="ri-bank-line" style={{ color: "#405189" }}></i>
                        Bank &amp; Cheque Details
                      </div>
                      <Row>
                        <Col md={4}>
                          <FormGroup>
                            <Label className="fw-medium small text-dark">Bank Name</Label>
                            <Input type="text" id="bankName" name="bankName"
                              value={formik.values.bankName} onChange={formik.handleChange}
                              placeholder="Enter bank name" style={{ borderRadius: "8px", fontSize: "0.875rem" }} />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label className="fw-medium small text-dark">Transaction Reference</Label>
                            <Input type="text" id="transactionReference" name="transactionReference"
                              value={formik.values.transactionReference} onChange={formik.handleChange}
                              placeholder="Enter reference no." style={{ borderRadius: "8px", fontSize: "0.875rem" }} />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label className="fw-medium small text-dark">Cheque Number</Label>
                            <Input type="text" id="chequeNo" name="chequeNo"
                              value={formik.values.chequeNo} onChange={formik.handleChange}
                              placeholder="e.g. 00123456" style={{ borderRadius: "8px", fontSize: "0.875rem" }} />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label className="fw-medium small text-dark">Cheque Date</Label>
                            <Flatpickr className="form-control"
                              value={formik.values.chequeDate}
                              onChange={(dates) => formik.setFieldValue("chequeDate", dates.length ? dates[0] : "")}
                              options={{ dateFormat: "Y-m-d" }}
                              style={{ borderRadius: "8px", fontSize: "0.875rem" }} />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label className="fw-medium small text-dark">Cheque Amount</Label>
                            <div style={{ position: "relative" }}>
                              <span style={{
                                position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)",
                                color: "#6c757d", fontSize: "0.875rem", zIndex: 1,
                              }}>Rs.</span>
                              <Input type="number" id="chequeAmount" name="chequeAmount"
                                value={formik.values.chequeAmount} onChange={formik.handleChange}
                                style={{ paddingLeft: "36px", borderRadius: "8px", fontSize: "0.875rem" }} />
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {/* Section 4 — Remarks */}
                  <div style={{ ...sectionStyle, marginBottom: 0 }}>
                    <div style={sectionHeaderStyle}>
                      <i className="ri-file-text-line" style={{ color: "#f7b84b" }}></i>
                      Additional Notes
                    </div>
                    <FormGroup>
                      <Label className="fw-medium small text-dark">Remarks</Label>
                      <Input
                        type="textarea" id="remarks" name="remarks"
                        value={formik.values.remarks} onChange={formik.handleChange}
                        placeholder="Add any notes or remarks..."
                        rows={3} style={{ borderRadius: "8px", fontSize: "0.875rem", resize: "none" }}
                      />
                    </FormGroup>
                  </div>

                </CardBody>
              </Card>

              {/* Footer Actions */}
              <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                <Button color="light" type="button" onClick={() => navigate(-1)}
                  style={{ borderRadius: "8px", fontWeight: "500", fontSize: "0.875rem", minWidth: "100px" }}>
                  Cancel
                </Button>
                <Button color="primary" type="submit"
                  style={{
                    borderRadius: "8px", fontWeight: "500", fontSize: "0.875rem",
                    minWidth: "160px",
                    background: "linear-gradient(135deg, #405189 0%, #0ab39c 100%)",
                    border: "none",
                  }}>
                  <i className="ri-check-line me-1"></i>
                  Submit Payment
                </Button>
              </div>
            </Col>

            {/* ─── RIGHT: Sticky Live Summary Panel ─── */}
            <Col lg={4}>
              <div style={{ position: "sticky", top: "80px" }}>

                {/* Summary Card */}
                <div style={{
                  borderRadius: "14px",
                  overflow: "hidden",
                  boxShadow: "0 4px 24px rgba(64,81,137,0.13)",
                  border: "1px solid #eef0f7",
                }}>
                  {/* Gradient header — live amount */}
                  <div style={{
                    background: "linear-gradient(135deg, #405189 0%, #0ab39c 100%)",
                    padding: "24px 20px 20px",
                    color: "#fff",
                  }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="ri-receipt-line" style={{ fontSize: "18px", opacity: 0.85 }}></i>
                      <span style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.8 }}>
                        Payment Summary
                      </span>
                    </div>
                    <div className="d-flex flex-column gap-1">
                      <div style={{ fontSize: "1.9rem", fontWeight: 700, letterSpacing: "-0.5px", lineHeight: 1.1 }}>
                        Rs.&nbsp;{Number(formik.values.paymentAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </div>
                      <div style={{ fontSize: "0.85rem", fontWeight: 500, opacity: 0.9, display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ opacity: 0.7 }}>out of</span>
                        <span style={{ borderBottom: "1px dashed rgba(255,255,255,0.4)", paddingBottom: "1px" }}>
                          Rs. {Number(totalDueAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                        <span style={{ opacity: 0.7 }}>remaining</span>
                      </div>
                    </div>
                    <div style={{ fontSize: "0.78rem", opacity: 0.75, marginTop: "12px", background: "rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: "4px", width: "fit-content" }}>
                      <i className="ri-calendar-event-line me-1"></i>
                      {formik.values.paymentDate || "Date not set"}
                    </div>
                  </div>

                  {/* Summary rows */}
                  <div style={{ background: "#fff" }}>
                    {[
                      {
                        icon: "ri-store-2-line", color: "#405189", label: "Supplier",
                        value: supplierOptions.find(o => o.value === formik.values.supplierId)?.label || "—",
                      },
                      {
                        icon: "ri-file-list-3-line", color: "#0ab39c", label: "Selected Bills",
                        value: formik.values.purchaseIds.length > 1 
                          ? `${formik.values.purchaseIds.length} Purchases Selected`
                          : (purchaseOptions.find(o => o.value === formik.values.purchaseIds[0])?.label || "—"),
                      },
                      {
                        icon: "ri-bank-card-line", color: "#f7b84b", label: "Payment Method",
                        value: paymentTypeOptions.find(o => o.value === formik.values.paymentTypeId)?.label || "—",
                      },
                      ...(formik.values.bankName ? [{
                        icon: "ri-bank-line", color: "#6c757d", label: "Bank", value: formik.values.bankName,
                      }] : []),
                      ...(formik.values.chequeNo ? [{
                        icon: "ri-coupon-3-line", color: "#6c757d", label: "Cheque No.", value: formik.values.chequeNo,
                      }] : []),
                    ].map((row, i, arr) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: "12px",
                        padding: "13px 18px",
                        borderBottom: i < arr.length - 1 ? "1px solid #f0f2f9" : "none",
                      }}>
                        <div style={{
                          width: "32px", height: "32px", borderRadius: "8px",
                          background: row.color + "18",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          <i className={row.icon} style={{ color: row.color, fontSize: "14px" }}></i>
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: "0.68rem", color: "#9aa1b9", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            {row.label}
                          </div>
                          <div style={{ fontSize: "0.84rem", fontWeight: 600, color: "#2e3650", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Remarks preview */}
                  {formik.values.remarks && (
                    <div style={{ padding: "14px 18px", background: "#f8f9fc", borderTop: "1px solid #eef0f7" }}>
                      <div style={{ fontSize: "0.68rem", color: "#9aa1b9", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px" }}>
                        Remarks
                      </div>
                      <p style={{ fontSize: "0.82rem", color: "#5a6372", margin: 0, lineHeight: 1.5 }}>
                        {formik.values.remarks}
                      </p>
                    </div>
                  )}
                </div>

                {/* Tips card */}
                <div style={{
                  marginTop: "16px", borderRadius: "12px", padding: "16px 18px",
                  background: "#fffbf0", border: "1px solid #fde8a0",
                }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <i className="ri-lightbulb-line" style={{ color: "#f7b84b", fontSize: "16px" }}></i>
                    <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#7a5e00", textTransform: "uppercase", letterSpacing: "0.08em" }}>Tips</span>
                  </div>
                  <ul style={{ fontSize: "0.8rem", color: "#6b5500", margin: 0, paddingLeft: "16px", lineHeight: 1.9 }}>
                    <li>Selecting a purchase auto-fills the amount.</li>
                    <li>Bank &amp; cheque fields appear for non-cash methods.</li>
                    <li>Required fields are marked <span className="text-danger fw-bold">*</span></li>
                  </ul>
                </div>

              </div>
            </Col>

          </Row>
        </form>
      </Container>
    </div>
  );
};

export default SupplierPaymentForm;

